import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import { Check, FileText, Pencil, Plus, RefreshCw, Search, Trash2, X } from 'lucide-react';
import { toast } from 'react-toastify';
import { Button } from '../components/ui/button';
import api from '../services/api';
import PageIntroDialog from '../components/PageIntroDialog';

const StyledCard = styled(Card)(({ theme }) => ({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: '16px',
  overflow: 'hidden',
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`
}));

const normalizeNullableId = (v) => {
  if (v == null || v === '') return null;
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.trunc(n);
};

const normalizeForm = (value) => ({
  id: value?.id != null ? Number(value.id) : null,
  fase_id: normalizeNullableId(value?.fase_id) || null,
  tipo_pratica_id: normalizeNullableId(value?.tipo_pratica_id),
  tipo_pratica_label: value?.tipo_pratica_label != null ? String(value.tipo_pratica_label) : null,
  creditore_id: normalizeNullableId(value?.creditore_id),
  creditore_label: value?.creditore_label != null ? String(value.creditore_label) : null,
  creditore_pec: value?.creditore_pec != null ? String(value.creditore_pec) : null,
  modello_id: value?.modello_id != null ? Number(value.modello_id) : null,
  nome_modello: value?.nome_modello != null ? String(value.nome_modello) : null,
  priorita: value?.priorita != null ? Number(value.priorita) : 0,
  attivo: value?.attivo == null ? true : Boolean(value.attivo)
});

const getTipoLabel = (t) => {
  if (!t) return '';
  return String(t.name || t.nome || t.label || t.descrizione || `Tipo pratica #${t.id}`);
};

const getCreditoreLabel = (c) => {
  if (!c) return '';
  const base = String(c.name || c.nome || `Creditore #${c.id}`);
  const pec = c.pec ? String(c.pec) : '';
  return pec ? `${base} — ${pec}` : base;
};

const getModelloLabel = (m) => {
  if (!m) return '';
  return String(m.nome_modello || m.nome || `Modello #${m.id}`);
};

export default function RegolePropostaModello() {
  const [introOpen, setIntroOpen] = useState(true);
  const [tab, setTab] = useState(0);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [onlyActive, setOnlyActive] = useState(false);

  const [fasi, setFasi] = useState([]);
  const [tipiPratica, setTipiPratica] = useState([]);
  const [creditori, setCreditori] = useState([]);

  const [modelli, setModelli] = useState([]);
  const [modelliLoading, setModelliLoading] = useState(false);
  const [modelliSearch, setModelliSearch] = useState('');

  const [previewForm, setPreviewForm] = useState({
    fase_id: null,
    tipo_pratica_id: null,
    creditore_id: null,
    creditore_label: null,
    creditore_pec: null
  });
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewHtmlLoading, setPreviewHtmlLoading] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(normalizeForm({ priorita: 0, attivo: true }));

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);

  const fetchLookups = useCallback(async () => {
    try {
      const [fasiRes, tipiRes] = await Promise.all([api.get('/richieste/fasi-gestione'), api.get('/category-negotiation')]);
      setFasi(Array.isArray(fasiRes.data?.data) ? fasiRes.data.data : []);
      setTipiPratica(Array.isArray(tipiRes.data) ? tipiRes.data : []);
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Errore caricamento lookup');
      setFasi([]);
      setTipiPratica([]);
    }
  }, []);

  const fetchCreditori = useCallback(async (q = '') => {
    try {
      const res = await api.get('/issuer', { params: q ? { q } : undefined });
      setCreditori(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Errore caricamento creditori');
      setCreditori([]);
    }
  }, []);

  const fetchRows = useCallback(async (searchArg = '', onlyActiveArg = false) => {
    setLoading(true);
    try {
      const res = await api.get('/richieste/regole-proposta-modello', {
        params: {
          search: String(searchArg || '').trim(),
          onlyActive: onlyActiveArg ? 1 : 0
        }
      });
      setRows(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Errore caricamento');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchModelli = useCallback(async (q = '') => {
    setModelliLoading(true);
    try {
      const res = await api.get('/modulistica', { params: { search: String(q || '').trim(), limit: 30 } });
      setModelli(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Errore caricamento modelli');
      setModelli([]);
    } finally {
      setModelliLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLookups();
    fetchCreditori('');
    fetchModelli('');
    fetchRows('', false);
  }, [fetchCreditori, fetchLookups, fetchModelli, fetchRows]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(0);
      fetchRows(search, onlyActive);
    }, 300);
    return () => clearTimeout(timer);
  }, [fetchRows, onlyActive, search]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchModelli(modelliSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [fetchModelli, modelliSearch]);

  const filteredRows = useMemo(() => (Array.isArray(rows) ? rows : []), [rows]);

  const pagedRows = useMemo(() => {
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;
    return filteredRows.slice(start, end);
  }, [filteredRows, page, rowsPerPage]);

  const fasiById = useMemo(() => new Map((fasi || []).map((f) => [Number(f.id), f])), [fasi]);
  const tipiById = useMemo(() => new Map((tipiPratica || []).map((t) => [Number(t.id), t])), [tipiPratica]);
  const credById = useMemo(() => new Map((creditori || []).map((c) => [Number(c.id), c])), [creditori]);
  const modelliById = useMemo(() => new Map((modelli || []).map((m) => [Number(m.id), m])), [modelli]);

  const selectedPreviewFase = useMemo(() => {
    if (!previewForm.fase_id) return null;
    return fasiById.get(Number(previewForm.fase_id)) || null;
  }, [fasiById, previewForm.fase_id]);

  const selectedPreviewTipo = useMemo(() => {
    if (!previewForm.tipo_pratica_id) return null;
    return tipiById.get(Number(previewForm.tipo_pratica_id)) || { id: Number(previewForm.tipo_pratica_id), name: `Tipo pratica #${Number(previewForm.tipo_pratica_id)}` };
  }, [previewForm.tipo_pratica_id, tipiById]);

  const selectedPreviewCreditore = useMemo(() => {
    if (!previewForm.creditore_id) return null;
    const found = credById.get(Number(previewForm.creditore_id));
    if (found) return found;
    if (previewForm.creditore_label) {
      return { id: Number(previewForm.creditore_id), name: previewForm.creditore_label, pec: previewForm.creditore_pec || '' };
    }
    return { id: Number(previewForm.creditore_id), name: `Creditore #${Number(previewForm.creditore_id)}` };
  }, [credById, previewForm.creditore_id, previewForm.creditore_label, previewForm.creditore_pec]);

  const selectedCreditore = useMemo(() => {
    if (!form.creditore_id) return null;
    const found = credById.get(Number(form.creditore_id));
    if (found) return found;
    if (form.creditore_label) {
      return { id: Number(form.creditore_id), name: form.creditore_label, pec: form.creditore_pec || '' };
    }
    return { id: Number(form.creditore_id), name: `Creditore #${Number(form.creditore_id)}` };
  }, [credById, form.creditore_id, form.creditore_label, form.creditore_pec]);

  const selectedTipoPratica = useMemo(() => {
    if (!form.tipo_pratica_id) return null;
    const found = tipiById.get(Number(form.tipo_pratica_id));
    if (found) return found;
    if (form.tipo_pratica_label) return { id: Number(form.tipo_pratica_id), name: form.tipo_pratica_label };
    return { id: Number(form.tipo_pratica_id), name: `Tipo pratica #${Number(form.tipo_pratica_id)}` };
  }, [form.tipo_pratica_id, form.tipo_pratica_label, tipiById]);

  const selectedModello = useMemo(() => {
    if (!form.modello_id) return null;
    const found = modelliById.get(Number(form.modello_id));
    if (found) return found;
    if (form.nome_modello) return { id: Number(form.modello_id), nome_modello: form.nome_modello };
    return { id: Number(form.modello_id), nome_modello: `Modello #${Number(form.modello_id)}` };
  }, [form.modello_id, form.nome_modello, modelliById]);

  const openNew = () => {
    setForm(normalizeForm({ priorita: 0, attivo: true }));
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setForm(normalizeForm(row));
    setDialogOpen(true);
  };

  const closeDialog = () => {
    if (saving) return;
    setDialogOpen(false);
    setForm(normalizeForm({ priorita: 0, attivo: true }));
  };

  const save = async () => {
    if (saving) return;
    const isEdit = Number.isInteger(form?.id) && form.id > 0;

    const payload = {
      fase_id: normalizeNullableId(form.fase_id),
      tipo_pratica_id: normalizeNullableId(form.tipo_pratica_id),
      creditore_id: normalizeNullableId(form.creditore_id),
      modello_id: normalizeNullableId(form.modello_id),
      priorita: Number.isFinite(Number(form.priorita)) ? Number(form.priorita) : 0,
      attivo: Boolean(form.attivo)
    };

    if (!payload.fase_id) {
      toast.error('Seleziona la fase');
      return;
    }
    if (!payload.modello_id) {
      toast.error('Seleziona il modello');
      return;
    }

    try {
      setSaving(true);
      if (isEdit) {
        await api.put(`/richieste/regole-proposta-modello/${form.id}`, payload);
        toast.success('Regola aggiornata');
      } else {
        await api.post('/richieste/regole-proposta-modello', payload);
        toast.success('Regola creata');
      }
      closeDialog();
      await fetchRows(search, onlyActive);
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Errore salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const openDelete = (row) => {
    setDeleteRow(row);
    setDeleteOpen(true);
  };

  const closeDelete = () => {
    if (deleting) return;
    setDeleteOpen(false);
    setDeleteRow(null);
  };

  const confirmDelete = async () => {
    if (deleting) return;
    const id = Number(deleteRow?.id);
    if (!Number.isFinite(id) || id <= 0) return;
    try {
      setDeleting(true);
      await api.delete(`/richieste/regole-proposta-modello/${id}`);
      toast.success('Regola eliminata');
      closeDelete();
      await fetchRows(search, onlyActive);
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Errore eliminazione');
    } finally {
      setDeleting(false);
    }
  };

  const headerSubtitle = useMemo(() => {
    const total = filteredRows.length;
    const act = filteredRows.filter((r) => Boolean(r.attivo)).length;
    return `${act} attive su ${total} regole`;
  }, [filteredRows]);

  const fetchPreview = useCallback(async (payload) => {
    const faseId = normalizeNullableId(payload?.fase_id);
    if (!faseId) {
      setPreviewData(null);
      return;
    }

    setPreviewLoading(true);
    try {
      const res = await api.get('/richieste/regole-proposta-modello/preview', {
        params: {
          fase_id: faseId,
          tipo_pratica_id: normalizeNullableId(payload?.tipo_pratica_id),
          creditore_id: normalizeNullableId(payload?.creditore_id)
        }
      });
      setPreviewData(res.data?.data || null);
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Errore anteprima');
      setPreviewData(null);
    } finally {
      setPreviewLoading(false);
    }
  }, []);

  const fetchPreviewHtml = useCallback(async (modelloId) => {
    const id = Number(modelloId);
    if (!Number.isFinite(id) || id <= 0) {
      setPreviewHtml('');
      return;
    }

    setPreviewHtmlLoading(true);
    try {
      const res = await api.get(`/modulistica/${id}`);
      setPreviewHtml(String(res.data?.content || ''));
    } catch (error) {
      toast.error(error.response?.data?.message || error.response?.data?.msg || error.message || 'Errore caricamento testo modello');
      setPreviewHtml('');
    } finally {
      setPreviewHtmlLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab !== 1) return;
    const timer = setTimeout(() => {
      fetchPreview(previewForm);
    }, 300);
    return () => clearTimeout(timer);
  }, [fetchPreview, previewForm, tab]);

  useEffect(() => {
    if (tab !== 1) return;
    const modelloId = previewData?.winner?.modello_id;
    if (!modelloId) {
      setPreviewHtml('');
      return;
    }
    fetchPreviewHtml(modelloId);
  }, [fetchPreviewHtml, previewData?.winner?.modello_id, tab]);

  return (
    <Box sx={{ p: 3 }}>
      <PageIntroDialog
        open={introOpen}
        onClose={() => setIntroOpen(false)}
        title="Regole Modelli"
        subtitle="Definisce quale modello proporre in base a fase, tipo pratica e creditore."
        icon={FileText}
        color="#6366f1"
        bullets={[
          'Regole generiche (tipo pratica NULL) o specifiche per tipo pratica',
          'Override per creditore (se valorizzato) con priorità più alta',
          'Usa la tab “Anteprima proposta” per simulare la scelta del modello'
        ]}
        example="Esempio: per fase “Richiesta Documenti” + tipo pratica “Carta” + creditore “Findomestic”, proponi il modello “Richiesta doc carta”."
      />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: alpha('#6366f1', 0.15),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <FileText size={20} color="#6366f1" />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.15 }}>
              Regole Modelli
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Tabella ponte fase + tipo pratica (+ creditore) → modello. {headerSubtitle}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
          <Button variant="outline" onClick={() => fetchRows(search, onlyActive)} title="Ricarica">
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button onClick={openNew} disabled={tab !== 0}>
            <Plus className="mr-2 h-4 w-4" />
            Nuova regola
          </Button>
        </Box>
      </Box>

      <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Regole" />
        <Tab label="Anteprima proposta" />
      </Tabs>

      {tab === 0 ? (
        <StyledCard>
          <CardContent>
            <Grid
              container
              spacing={2}
              sx={{
                mb: 3,
                flexWrap: 'nowrap',
                overflowX: 'auto',
                '&::-webkit-scrollbar': { height: 6 }
              }}
            >
              <Grid item sx={{ minWidth: 420, flex: '0 0 auto' }}>
                <TextField
                  placeholder="Cerca fase o modello..."
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search size={18} />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              <Grid item sx={{ minWidth: 220, flex: '0 0 auto', display: 'flex', alignItems: 'center' }}>
                <FormControlLabel
                  label="Solo attive"
                  sx={{ m: 0 }}
                  control={<Checkbox checked={onlyActive} onChange={(e) => setOnlyActive(Boolean(e.target.checked))} />}
                />
              </Grid>
            </Grid>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, width: 220 }}>Fase</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: 260 }}>Tipo pratica</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: 340 }}>Creditore</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Modello</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: 110, textAlign: 'right' }}>Priorità</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: 90 }}>Attivo</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: 120, textAlign: 'right' }}>Azioni</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ py: 6 }}>
                        <Typography align="center" color="text.secondary">
                          Caricamento...
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : pagedRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ py: 6 }}>
                        <Typography align="center" color="text.secondary">
                          Nessun record trovato
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    pagedRows.map((row) => {
                      const fase = fasiById.get(Number(row.fase_id));
                      const tipoFallback = row.tipo_pratica_id ? tipiById.get(Number(row.tipo_pratica_id)) : null;
                      const credFallback = row.creditore_id ? credById.get(Number(row.creditore_id)) : null;
                      const modelloFallback = modelliById.get(Number(row.modello_id));

                      const faseLabel = fase ? String(fase.nome || fase.codice) : String(row.fase_nome || row.fase_codice || `Fase #${row.fase_id}`);
                      const tipoLabel = row.tipo_pratica_label || getTipoLabel(tipoFallback) || '—';
                      const credLabel = row.creditore_label
                        ? row.creditore_pec
                          ? `${row.creditore_label} — ${row.creditore_pec}`
                          : row.creditore_label
                        : row.creditore_id
                          ? getCreditoreLabel(credFallback) || `Creditore #${row.creditore_id}`
                          : '—';
                      const modelloLabel = String(row.nome_modello || getModelloLabel(modelloFallback) || `Modello #${row.modello_id}`);

                      return (
                        <TableRow key={row.id} hover>
                          <TableCell>
                            <Paper
                              variant="outlined"
                              sx={(theme) => ({
                                display: 'inline-flex',
                                alignItems: 'center',
                                px: 1.25,
                                py: 0.5,
                                borderRadius: '999px',
                                fontWeight: 800,
                                fontSize: 12,
                                bgcolor: theme.palette.mode === 'dark' ? alpha('#0b1220', 0.6) : '#f8fafc'
                              })}
                            >
                              {faseLabel}
                            </Paper>
                          </TableCell>
                          <TableCell>{tipoLabel}</TableCell>
                          <TableCell>{credLabel}</TableCell>
                          <TableCell>{modelloLabel}</TableCell>
                          <TableCell align="right">{Number(row.priorita || 0)}</TableCell>
                          <TableCell>{row.attivo ? 'Sì' : 'No'}</TableCell>
                          <TableCell align="right">
                            <Tooltip title="Modifica" arrow>
                              <span>
                                <IconButton size="small" onClick={() => openEdit(row)}>
                                  <Pencil size={16} />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Elimina" arrow>
                              <span>
                                <IconButton size="small" onClick={() => openDelete(row)} sx={{ color: '#ef4444' }}>
                                  <Trash2 size={16} />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={filteredRows.length}
              page={page}
              onPageChange={(_e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                const next = parseInt(e.target.value, 10) || 25;
                setRowsPerPage(next);
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage="Righe per pagina:"
              labelDisplayedRows={({ from, to, count }) => `${from}–${to} di ${count !== -1 ? count : `più di ${to}`}`}
            />
          </CardContent>
        </StyledCard>
      ) : (
        <StyledCard>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>
              Simula una pratica
            </Typography>
            <Grid
              container
              spacing={2}
              sx={{
                mb: 3,
                flexWrap: 'nowrap',
                overflowX: 'auto',
                '&::-webkit-scrollbar': { height: 6 }
              }}
            >
              <Grid item sx={{ minWidth: 320, flex: '0 0 auto' }}>
                <Autocomplete
                  options={Array.isArray(fasi) ? fasi : []}
                  value={selectedPreviewFase}
                  onChange={(_e, v) => setPreviewForm((prev) => ({ ...prev, fase_id: v?.id != null ? Number(v.id) : null }))}
                  getOptionLabel={(o) => String(o?.nome || o?.codice || '')}
                  isOptionEqualToValue={(a, b) => Number(a?.id) === Number(b?.id)}
                  renderInput={(params) => <TextField {...params} label="Fase" size="small" />}
                />
              </Grid>
              <Grid item sx={{ minWidth: 320, flex: '0 0 auto' }}>
                <Autocomplete
                  options={Array.isArray(tipiPratica) ? tipiPratica : []}
                  value={selectedPreviewTipo}
                  onChange={(_e, v) => setPreviewForm((prev) => ({ ...prev, tipo_pratica_id: v?.id != null ? Number(v.id) : null }))}
                  getOptionLabel={(o) => getTipoLabel(o)}
                  isOptionEqualToValue={(a, b) => Number(a?.id) === Number(b?.id)}
                  renderInput={(params) => <TextField {...params} label="Tipo pratica" size="small" />}
                />
              </Grid>
              <Grid item sx={{ minWidth: 360, flex: '0 0 auto' }}>
                <Autocomplete
                  options={Array.isArray(creditori) ? creditori : []}
                  value={selectedPreviewCreditore}
                  onChange={(_e, v) => {
                    setPreviewForm((prev) => ({
                      ...prev,
                      creditore_id: v?.id != null ? Number(v.id) : null,
                      creditore_label: v?.name != null ? String(v.name) : v?.nome != null ? String(v.nome) : null,
                      creditore_pec: v?.pec != null ? String(v.pec) : null
                    }));
                    if (v?.id != null) {
                      setCreditori((prev) => {
                        const merged = [...(Array.isArray(prev) ? prev : []), v];
                        const byId = new Map();
                        merged.forEach((c) => {
                          if (c?.id != null) byId.set(String(c.id), c);
                        });
                        return Array.from(byId.values());
                      });
                    }
                  }}
                  getOptionLabel={(o) => getCreditoreLabel(o)}
                  isOptionEqualToValue={(a, b) => Number(a?.id) === Number(b?.id)}
                  onInputChange={(_e, value, reason) => {
                    if (reason !== 'input') return;
                    const q = String(value || '').trim();
                    if (!q) return;
                    fetchCreditori(q);
                  }}
                  renderInput={(params) => <TextField {...params} label="Creditore" size="small" />}
                />
              </Grid>
            </Grid>

            {previewLoading ? (
              <Typography color="text.secondary">Calcolo proposta...</Typography>
            ) : previewData?.winner ? (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Box sx={{ mt: 0.25 }}>
                    <FileText size={18} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography sx={{ fontWeight: 900 }}>{String(previewData.winner.nome_modello || '')}</Typography>
                      <Chip size="small" label="proposto" color="success" variant="filled" />
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      Regola: {previewData.winner.tipo_pratica_label || '—'} / {previewData.winner.creditore_label || '—'} · priorità{' '}
                      {Number(previewData.winner.priorita || 0)}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                    Testo modello
                  </Typography>
                  {previewHtmlLoading ? (
                    <Typography color="text.secondary">Caricamento testo...</Typography>
                  ) : previewHtml ? (
                    <Paper
                      variant="outlined"
                      sx={{
                        borderRadius: '16px',
                        p: 2,
                        maxHeight: 420,
                        overflow: 'auto',
                        '& img': { maxWidth: '100%' }
                      }}
                    >
                      <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
                    </Paper>
                  ) : (
                    <Typography color="text.secondary">Nessun testo disponibile per questo modello</Typography>
                  )}
                </Box>
              </Box>
            ) : previewForm.fase_id ? (
              <Typography color="text.secondary">Nessuna regola applicabile</Typography>
            ) : (
              <Typography color="text.secondary">Seleziona almeno la fase per calcolare la proposta</Typography>
            )}
          </CardContent>
        </StyledCard>
      )}

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogTitle sx={{ fontWeight: 800 }}>
          {Number.isInteger(form?.id) && form.id > 0 ? 'Modifica regola' : 'Nuova regola'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={Array.isArray(fasi) ? fasi : []}
                value={form.fase_id ? fasiById.get(Number(form.fase_id)) || null : null}
                onChange={(_e, v) => setForm((prev) => ({ ...prev, fase_id: v?.id != null ? Number(v.id) : null }))}
                getOptionLabel={(o) => String(o?.nome || o?.codice || '')}
                isOptionEqualToValue={(a, b) => Number(a?.id) === Number(b?.id)}
                renderInput={(params) => <TextField {...params} label="Fase *" size="small" />}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={Array.isArray(tipiPratica) ? tipiPratica : []}
                value={selectedTipoPratica}
                onChange={(_e, v) =>
                  setForm((prev) => ({
                    ...prev,
                    tipo_pratica_id: v?.id != null ? Number(v.id) : null,
                    tipo_pratica_label: v ? String(getTipoLabel(v)) : null
                  }))
                }
                getOptionLabel={(o) => getTipoLabel(o)}
                isOptionEqualToValue={(a, b) => Number(a?.id) === Number(b?.id)}
                renderInput={(params) => <TextField {...params} label="Tipo pratica (opz.)" size="small" />}
              />
            </Grid>

            <Grid item xs={12} sm={8}>
              <Autocomplete
                options={Array.isArray(creditori) ? creditori : []}
                value={selectedCreditore}
                onChange={(_e, v) =>
                  setForm((prev) => ({
                    ...prev,
                    creditore_id: v?.id != null ? Number(v.id) : null,
                    creditore_label: v ? String(v.name || v.nome || prev.creditore_label || '') : null,
                    creditore_pec: v ? String(v.pec || '') || null : null
                  }))
                }
                getOptionLabel={(o) => getCreditoreLabel(o)}
                isOptionEqualToValue={(a, b) => Number(a?.id) === Number(b?.id)}
                onInputChange={(_e, value) => {
                  const q = String(value || '').trim();
                  if (!q) return;
                  fetchCreditori(q);
                }}
                renderInput={(params) => <TextField {...params} label="Creditore (opz.)" size="small" />}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                label="Priorità"
                size="small"
                fullWidth
                type="number"
                value={Number.isFinite(Number(form.priorita)) ? Number(form.priorita) : 0}
                onChange={(e) => setForm((prev) => ({ ...prev, priorita: Number(e.target.value || 0) }))}
              />
            </Grid>

            <Grid item xs={12} sm={8}>
              <Autocomplete
                options={Array.isArray(modelli) ? modelli : []}
                value={selectedModello}
                onChange={(_e, v) =>
                  setForm((prev) => ({
                    ...prev,
                    modello_id: v?.id != null ? Number(v.id) : null,
                    nome_modello: v ? String(getModelloLabel(v)) : null
                  }))
                }
                getOptionLabel={(o) => getModelloLabel(o)}
                isOptionEqualToValue={(a, b) => Number(a?.id) === Number(b?.id)}
                loading={modelliLoading}
                onInputChange={(_e, value) => setModelliSearch(String(value || '').trim())}
                renderInput={(params) => <TextField {...params} label="Modello *" size="small" />}
              />
            </Grid>

            <Grid item xs={12} sm={4} sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControlLabel
                label="Attivo"
                control={<Checkbox checked={Boolean(form.attivo)} onChange={(e) => setForm((prev) => ({ ...prev, attivo: Boolean(e.target.checked) }))} />}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button variant="outline" onClick={closeDialog} disabled={saving}>
            <X className="mr-2 h-4 w-4" />
            Annulla
          </Button>
          <Button onClick={save} disabled={saving}>
            <Check className="mr-2 h-4 w-4" />
            Salva
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={closeDelete} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogTitle sx={{ fontWeight: 800 }}>Elimina regola</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary">
            Confermi eliminazione della regola #{Number(deleteRow?.id || 0)}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outline" onClick={closeDelete} disabled={deleting}>
            <X className="mr-2 h-4 w-4" />
            Annulla
          </Button>
          <Button onClick={confirmDelete} disabled={deleting}>
            <Trash2 className="mr-2 h-4 w-4" />
            Elimina
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
